/**
 * Générateur — Utiliser le vocabulaire des probabilités dans des contextes
 * concrets : expérience aléatoire, issue, évènement.
 * Attendu ID : 978 | Niveau : 5ème | Domaine : Probabilités (descripteur 132)
 *
 * Version conforme à la spécification 978 (2026-09-21).
 * Supprimés : les trois formats de calcul de probabilité
 * (`urne_texte_trou_saisie`, `roue_texte_trou_saisie`, `sac_texte_trou_saisie`)
 * et toutes les lignes « P(...) = k/8 » des vrais/faux — `exclusions` 3 :
 * « aucun calcul de probabilité : le mot lui-même ne figure pas dans la liste
 * de vocabulaire de cet objectif, et le quotient relève de l'objectif voisin ».
 * Supprimées aussi : toutes les questions d'évènement CONTRAIRE (`exclusions`
 * 1) et la carte « Le sac de 200 billes » — 100 à 500 objets contre 40 au plus
 * (`variables.nombre_d_objets_de_la_population`), et son libellé nomme le
 * nombre hors bornes.
 * Ajoutées : « Le sac de jetons » (population dans les bornes), « Issue,
 * évènement, ni l'un ni l'autre » (niveau 3 de la graduation) et
 * « L'évènement sur mesure » (niveau 4).
 *
 * Plan de cartes :
 *   « L'urne de 8 boules » (niveaux 1 et 2) — 8 boules numérotées, TROIS
 *     couleurs d'effectifs différents dont une seule boule d'une couleur :
 *     l'expérience porte sur la couleur, donc trois issues pour huit objets.
 *   « La roue de loterie » (niveaux 1 et 2) — 8 secteurs identiques, QUATRE
 *     gains distincts dont un sur un seul secteur : les issues ne se répètent
 *     pas même quand les secteurs se répètent.
 *   « Le sac de jetons » (niveaux 1 et 2) — 12 à 40 jetons, quatre couleurs
 *     d'effectifs distincts dont une à un seul jeton.
 *   « Issue, évènement, ni l'un ni l'autre » (niveau 3) — trois formulations à
 *     classer : l'une désigne une issue, l'autre un évènement, la troisième
 *     ne décrit pas un résultat du tirage.
 *   « L'évènement sur mesure » (niveau 4) — reconnaître la formulation d'un
 *     évènement réalisé par EXACTEMENT deux issues, puis par une seule, et
 *     cocher les issues qui le réalisent.
 *
 * Spécification appliquée :
 *   Bornes : 2 à 8 issues par expérience ; 5 à 40 objets dans la population,
 *     toujours au moins le DOUBLE du nombre d'issues ; au moins deux catégories
 *     d'effectifs différents, et une catégorie à un seul objet pour que l'issue
 *     rare puisse être oubliée ; nombre d'issues réalisant un évènement entre 1
 *     et le nombre d'issues moins un ; 3 questions par expérience.
 *   Exclusions : aucun évènement contraire (toutes les questions « quelle
 *     phrase sans négation décrit l'évènement contraire » ont disparu) ; aucune
 *     notation ensembliste ni aucun « P(...) » ; aucun calcul de probabilité ;
 *     aucune situation dont l'issue est connue d'avance, sauf comme cas à
 *     trancher dans la question « est-ce une expérience aléatoire ? » ; aucune
 *     expérience à deux épreuves.
 *   Erreurs visées → mots-clés (`_meta.params.distracteurs[].code`) :
 *     - confondre une issue et un objet → `issue_confondue_avec_objet`
 *     - confondre un évènement et une issue → `evenement_confondu_avec_issue`
 *     - mal reconnaître une expérience aléatoire → `experience_aleatoire_mal_reconnue`
 *     - oublier une issue d'effectif faible → `issue_rare_oubliee`
 *     - répondre par un nombre au lieu de la liste, ou l'inverse → `nombre_au_lieu_de_la_liste`
 *     - refuser d'appeler évènement ce qui ne tient qu'à une issue → `evenement_elementaire_refuse`
 *   Graduation → cartes : 1 et 2 « L'urne de 8 boules », « La roue de loterie »,
 *     « Le sac de jetons » ; 3 « Issue, évènement, ni l'un ni l'autre » ;
 *     4 « L'évènement sur mesure ».
 *   Non tranché : la rédaction libre d'un évènement (niveau 4) n'est pas
 *     automatisable ; elle est servie en reconnaissance. Un évènement réalisé
 *     par plusieurs issues est décrit en langue naturelle (« rouge ou jaune ») :
 *     la spec l'exige au niveau 2 tout en excluant « la réunion », que nous
 *     lisons comme l'opération et sa notation, non comme la description
 *     ordinaire d'un évènement.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeClasser,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';
import { svgUrne, svgRoue } from './svgProbas';

const ATTENDU_ID = 978;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_URNE = 'L\'urne de 8 boules';
const L_ROUE = 'La roue de loterie';
const L_JETONS = 'Le sac de jetons';
const L_PHRASES = 'Issue, évènement, ni l\'un ni l\'autre';
const L_MESURE = 'L\'évènement sur mesure';

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** Garantit qu'un tableau vrai/faux n'est ni tout vrai ni tout faux. */
const equilibrer = (vs) => {
    const nbVrai = vs.filter(Boolean).length;
    if (nbVrai === 0) vs[0] = true;
    else if (nbVrai === vs.length) vs[0] = false;
    return vs;
};

/** Situations dont le résultat est connu d'avance : elles ne servent qu'à la
 *  question « est-ce une expérience aléatoire ? », posée comme cas à trancher. */
const SITUATIONS_CONNUES = [
    'On compte le nombre de côtés d\'un carré dessiné au tableau.',
    'On regarde quel mois vient juste après le mois de mars.',
    'On calcule 12 + 19 et on note le résultat.',
    'On relit l\'heure affichée par une horloge arrêtée depuis hier.',
    'On compte le nombre de faces d\'un dé cubique posé sur la table.',
    'On regarde la lettre qui suit le M dans l\'alphabet.',
];

// ─── Les trois dispositifs (même structure, trois contextes) ───

const COULEURS_URNE = ['rouge', 'bleue', 'verte', 'jaune', 'violette', 'noire'];
const REPARTITIONS_URNE = [[4, 3, 1], [5, 2, 1], [3, 4, 1], [2, 5, 1], [6, 1, 1]];

/** Urne : 8 boules numérotées, trois couleurs, une couleur à UNE seule boule.
 *  L'expérience porte sur la COULEUR : trois issues pour huit objets. */
const tirerUrne = () => {
    const parts = randomChoice(REPARTITIONS_URNE);
    const melange = shuffle([...COULEURS_URNE]);
    const couleurs = melange.slice(0, 3);
    const absentes = melange.slice(3, 6);
    const numeros = shuffle([1, 2, 3, 4, 5, 6, 7, 8]);
    const boules = [];
    let k = 0;
    parts.forEach((n, i) => {
        for (let j = 0; j < n; j++) { boules.push({ couleur: couleurs[i], numero: numeros[k] }); k += 1; }
    });
    return {
        label: L_URNE,
        intro: '<p>Une urne contient 8 boules numérotées, identiques au toucher : '
            + `${parts.map((n, i) => `${n} ${couleurs[i]}${n > 1 ? 's' : ''}`).join(', ')}. `
            + 'On tire une boule au hasard et on note sa COULEUR.</p>',
        el: imgElement(svgUrne(shuffle(boules)), 'compact'),
        issues: couleurs,
        effectifs: parts,
        objets: 8,
        absentes,
        nomObjet: 'boule',
        nomCategorie: 'couleurs',
        phraseContenant: "L'urne contient 8 boules.",
        nomIssue: (c) => `la boule tirée est ${c}`,
        issueSeule: (c) => `Obtenir une boule ${c}`,
    };
};

const GAINS_POOL = [0, 5, 10, 20, 50, 100];

/** Roue : 8 secteurs identiques, quatre gains distincts dont un sur un seul
 *  secteur, aucune multiplicité au-delà de 3 (le nombre d'issues, 4, ne peut
 *  donc jamais coïncider avec un effectif). */
const tirerRoue = () => {
    for (let essai = 0; essai < 120; essai++) {
        const melange = shuffle([...GAINS_POOL]);
        const valeurs = melange.slice(0, 4).sort((a, b) => a - b);
        const absentes = melange.slice(4);
        const mult = [1, 1, 1, 1];
        for (let r = 0; r < 4; r++) mult[randInt(0, 3)] += 1;
        if (Math.max(...mult) > 3 || Math.min(...mult) !== 1) continue;
        if (new Set(mult).size === 1) continue;
        const secteurs = shuffle(valeurs.flatMap((v, i) => Array(mult[i]).fill(v)));
        return {
            label: L_ROUE,
            intro: '<p>Une roue de loterie est partagée en 8 secteurs identiques. Chaque secteur '
                + 'indique un gain en euros. On fait tourner la roue et on note le GAIN obtenu.</p>',
            el: imgElement(svgRoue(secteurs.map(String)), 'compact'),
            issues: valeurs.map((v) => `${v} €`),
            effectifs: mult,
            objets: 8,
            absentes: absentes.map((v) => `${v} €`),
            nomObjet: 'secteur',
            nomCategorie: 'gains différents',
            phraseContenant: 'La roue compte 8 secteurs.',
            nomIssue: (g) => `le gain obtenu est de ${g}`,
            issueSeule: (g) => `Obtenir ${g}`,
        };
    }
    const valeurs = [0, 5, 20, 50];
    const mult = [3, 2, 2, 1];
    return {
        label: L_ROUE,
        intro: '<p>Une roue de loterie est partagée en 8 secteurs identiques. Chaque secteur '
            + 'indique un gain en euros. On fait tourner la roue et on note le GAIN obtenu.</p>',
        el: imgElement(svgRoue([0, 0, 0, 5, 5, 20, 20, 50].map(String)), 'compact'),
        issues: valeurs.map((v) => `${v} €`),
        effectifs: mult,
        objets: 8,
        absentes: [10, 100].map((v) => `${v} €`),
        nomObjet: 'secteur',
        nomCategorie: 'gains différents',
        phraseContenant: 'La roue compte 8 secteurs.',
        nomIssue: (g) => `le gain obtenu est de ${g}`,
        issueSeule: (g) => `Obtenir ${g}`,
    };
};

const COULEURS_JETONS = ['rouges', 'bleus', 'verts', 'jaunes', 'violets', 'noirs'];
const SINGULIER_JETON = { rouges: 'rouge', bleus: 'bleu', verts: 'vert', jaunes: 'jaune', violets: 'violet', noirs: 'noir' };

/** Sac : 12 à 40 jetons, quatre couleurs d'effectifs DISTINCTS dont une à un
 *  seul jeton ; aucun effectif n'égale le nombre d'issues. */
const tirerJetons = () => {
    for (let essai = 0; essai < 200; essai++) {
        const melange = shuffle([...COULEURS_JETONS]);
        const couleurs = melange.slice(0, 4);
        const absentes = melange.slice(4);
        const autres = shuffle([3, 5, 6, 7, 8, 9, 10, 11, 12]).slice(0, 3);
        const effectifs = shuffle([1, ...autres]);
        const total = effectifs.reduce((a, b) => a + b, 0);
        if (total < 12 || total > 40) continue;
        if (new Set(effectifs).size !== 4) continue;
        if (effectifs.includes(4) || effectifs.includes(total)) continue;
        return {
            label: L_JETONS,
            intro: `<p>Un sac opaque contient ${total} jetons indiscernables au toucher : `
                + `${effectifs.map((n, i) => `${n} ${n > 1 ? couleurs[i] : SINGULIER_JETON[couleurs[i]]}`).join(', ')}. `
                + 'On tire un jeton au hasard et on note sa COULEUR.</p>',
            el: null,
            issues: couleurs.map((c) => SINGULIER_JETON[c]),
            effectifs,
            objets: total,
            absentes: absentes.map((c) => SINGULIER_JETON[c]),
            nomObjet: 'jeton',
            nomCategorie: 'couleurs',
            phraseContenant: `Le sac contient ${total} jetons.`,
            nomIssue: (c) => `le jeton tiré est ${c}`,
            issueSeule: (c) => `Obtenir un jeton ${c}`,
        };
    }
    return {
        label: L_JETONS,
        intro: '<p>Un sac opaque contient 21 jetons indiscernables au toucher : 1 rouge, 5 bleus, '
            + '7 verts, 8 jaunes. On tire un jeton au hasard et on note sa COULEUR.</p>',
        el: null,
        issues: ['rouge', 'bleu', 'vert', 'jaune'],
        effectifs: [1, 5, 7, 8],
        objets: 21,
        absentes: ['violet', 'noir'],
        nomObjet: 'jeton',
        nomCategorie: 'couleurs',
        phraseContenant: 'Le sac contient 21 jetons.',
        nomIssue: (c) => `le jeton tiré est ${c}`,
        issueSeule: (c) => `Obtenir un jeton ${c}`,
    };
};

// ─── Paramètres communs ───

const paramsDispo = (d, niveau, terme, extra = {}) => ({
    niveau,
    terme_du_vocabulaire_travaille: terme,
    nombre_d_issues_de_l_experience: d.issues.length,
    nombre_d_objets_de_la_population: d.objets,
    nombre_d_objets_par_categorie: d.effectifs,
    nombre_de_questions_par_experience: 3,
    ...extra,
});

/** Deux issues forment l'évènement composé, nommées sans aucun calcul. */
const evenementCompose = (d) => {
    const [i, j] = shuffle(d.issues.map((_, x) => x)).slice(0, 2).sort((a, b) => a - b);
    return {
        indices: [i, j],
        phrase: `${cap(d.nomIssue(d.issues[i]))} ou ${d.issues[j]}`,
        courte: `${d.issues[i]} ou ${d.issues[j]}`,
    };
};

// ─── Fabriques de formats, communes aux trois dispositifs ───

const qcmVocabulaire = (d) => {
    const v = randInt(0, 2);
    if (v === 0) {
        const bonne = String(d.issues.length);
        const dist = [
            { code: 'issue_confondue_avec_objet', valeur: String(d.objets) },
            { code: 'issue_confondue_avec_objet', valeur: String(Math.max(...d.effectifs)) },
            { code: 'nombre_au_lieu_de_la_liste', valeur: d.issues.join(', ') },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `${d.intro}<p>Combien cette expérience aléatoire a-t-elle d'ISSUES différentes ? `
            + 'Réponds par un nombre.</p>',
            opts(d.label));
        if (d.el) payload.elements_additionnels = d.el;
        payload._meta.params = paramsDispo(d, 1, 'issue', { distracteurs: dist });
        return payload;
    }
    if (v === 1) {
        if (randInt(0, 1) === 0) {
            const bonne = 'Oui : on connaît toutes les issues possibles, mais pas laquelle va sortir';
            const dist = [
                { code: 'experience_aleatoire_mal_reconnue', valeur: 'Non : on connaît déjà toutes les issues possibles, donc il n\'y a pas de hasard' },
                { code: 'experience_aleatoire_mal_reconnue', valeur: 'Non : une expérience aléatoire doit avoir un nombre d\'issues inconnu' },
                { code: 'experience_aleatoire_mal_reconnue', valeur: 'Oui, et le résultat est connu d\'avance' },
            ];
            const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
                `${d.intro}<p>Est-ce une expérience aléatoire ?</p>`,
                opts(d.label));
            if (d.el) payload.elements_additionnels = d.el;
            payload._meta.params = paramsDispo(d, 1, 'expérience aléatoire', { distracteurs: dist });
            return payload;
        }
        const s = randomChoice(SITUATIONS_CONNUES);
        const bonne = 'Non : le résultat est connu d\'avance, il n\'y a qu\'une seule issue possible';
        const dist = [
            { code: 'experience_aleatoire_mal_reconnue', valeur: 'Oui : tant qu\'on n\'a pas regardé, le résultat est au hasard' },
            { code: 'experience_aleatoire_mal_reconnue', valeur: 'Oui : toute situation que l\'on observe est une expérience aléatoire' },
            { code: 'experience_aleatoire_mal_reconnue', valeur: 'Oui : le résultat dépend de la personne qui regarde' },
        ];
        const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
            `<p>${s}</p><p>Est-ce une expérience aléatoire ?</p>`,
            opts(d.label));
        payload._meta.params = paramsDispo(d, 1, 'expérience aléatoire', {
            situation_a_resultat_connu: true,
            distracteurs: dist,
        });
        return payload;
    }
    const i = randInt(0, d.issues.length - 1);
    const bonne = cap(d.issues[i]);
    const dist = shuffle(d.issues.filter((_, x) => x !== i)).slice(0, 3)
        .map((x) => ({ code: 'evenement_confondu_avec_issue', valeur: cap(x) }));
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${d.intro}<p>On considère l'évènement « ${d.issueSeule(d.issues[i])} ». `
        + 'Quelle issue réalise cet évènement ?</p>',
        opts(d.label));
    if (d.el) payload.elements_additionnels = d.el;
    payload._meta.params = paramsDispo(d, 2, 'évènement', {
        nombre_d_issues_realisant_l_evenement: 1,
        nature_de_l_evenement: 'évènement réalisé par une seule issue',
        distracteurs: dist,
    });
    return payload;
};

const qcmMultipleVocabulaire = (d) => {
    if (randInt(0, 1) === 0) {
        const corrects = d.issues.map(cap);
        const choix = shuffle([...corrects, ...d.absentes.slice(0, 2).map(cap)]);
        const payload = makeQcm(choix, corrects,
            `${d.intro}<p>Coche toutes les ISSUES de cette expérience, sans en oublier aucune : `
            + `il y en a ${d.issues.length}.</p>`,
            opts(d.label), true);
        if (d.el) payload.elements_additionnels = d.el;
        payload._meta.params = paramsDispo(d, 1, 'issue', { erreurVisee: 'issue_rare_oubliee' });
        return payload;
    }
    const ev = evenementCompose(d);
    const corrects = ev.indices.map((x) => cap(d.issues[x]));
    const choix = shuffle(d.issues.map(cap));
    const dist = d.issues
        .filter((_, x) => !ev.indices.includes(x))
        .map((x) => ({ code: 'evenement_confondu_avec_issue', valeur: cap(x) }));
    const payload = makeQcm(choix, corrects,
        `${d.intro}<p>On considère l'évènement « ${ev.phrase} ». `
        + 'Coche toutes les issues qui réalisent cet évènement.</p>',
        opts(d.label), true);
    if (d.el) payload.elements_additionnels = d.el;
    payload._meta.params = paramsDispo(d, 2, 'évènement', {
        nombre_d_issues_realisant_l_evenement: 2,
        nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
        distracteurs: dist,
    });
    return payload;
};

const vfVocabulaire = (d) => {
    const i = randInt(0, d.issues.length - 1);
    const ev = evenementCompose(d);
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('issue_confondue_avec_objet');
    if (!vs[1]) codes.push('evenement_elementaire_refuse');
    if (!vs[2]) codes.push('evenement_confondu_avec_issue');
    if (!vs[3]) codes.push('experience_aleatoire_mal_reconnue');
    const lignes = [
        [`Cette expérience a ${vs[0] ? d.issues.length : d.objets} issues différentes.`, vs[0] ? 0 : 1],
        [vs[1]
            ? `« ${d.issueSeule(d.issues[i])} » est un évènement, même s'il n'est réalisé que par une seule issue.`
            : `« ${d.issueSeule(d.issues[i])} » ne peut pas être appelé un évènement, car il ne regroupe qu'une seule issue.`, vs[1] ? 0 : 1],
        [`L'évènement « ${ev.phrase} » est réalisé par ${vs[2] ? 'deux issues' : 'une seule issue'}.`, vs[2] ? 0 : 1],
        [vs[3]
            ? 'Connaître toutes les issues possibles d\'une expérience, ce n\'est pas connaître son résultat.'
            : 'Dès qu\'on connaît toutes les issues possibles, l\'expérience n\'est plus aléatoire.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${d.intro}<p>Vrai ou faux ?</p>`,
        { ...opts(d.label), ...(d.el ? { elements_additionnels: d.el } : {}) });
    payload._meta.params = paramsDispo(d, 2, 'évènement', {
        nombre_d_issues_realisant_l_evenement: 2,
        nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « Issue, évènement, ni l'un ni l'autre » (niveau 3)
// ═════════════════════════════════════════════════════════════

/** Trois formulations sur un même dispositif : l'une désigne une issue,
 *  l'autre un évènement réalisé par deux issues, la troisième décrit le
 *  dispositif et non un résultat du tirage. */
const tirerPhrases = () => {
    const d = randomChoice([tirerUrne, tirerJetons, tirerRoue])();
    const i = randInt(0, d.issues.length - 1);
    const ev = evenementCompose(d);
    const niNi = [
        d.phraseContenant,
        `Il y a ${d.issues.length} ${d.nomCategorie} en tout.`,
        d.nomObjet === 'secteur'
            ? 'On fait tourner la roue sans la regarder.'
            : `On tire le ${d.nomObjet} sans regarder dans le sac.`,
    ];
    return {
        d,
        issue: cap(d.nomIssue(d.issues[i])),
        evenement: ev.phrase,
        niNi: randomChoice(niNi),
        nbIssuesEvenement: 2,
    };
};

const CAT_ISSUE = 'Cette phrase désigne une ISSUE';
const CAT_EVT = 'Cette phrase désigne un ÉVÈNEMENT réalisé par plusieurs issues';
const CAT_NI = 'Cette phrase ne décrit pas un résultat du tirage';

const genPhrasesClasser = () => {
    const p = tirerPhrases();
    const payload = makeClasser([
        { name: CAT_ISSUE, items: [p.issue] },
        { name: CAT_EVT, items: [p.evenement] },
        { name: CAT_NI, items: [p.niNi] },
    ],
    `${p.d.intro}<p>Classe ces trois phrases : l'une désigne une issue, l'autre un évènement `
        + 'réalisé par plusieurs issues, la troisième ne décrit pas un résultat du tirage.</p>',
    opts(L_PHRASES));
    if (p.d.el) payload.elements_additionnels = p.d.el;
    payload._meta.params = paramsDispo(p.d, 3, 'évènement', {
        nombre_d_issues_realisant_l_evenement: p.nbIssuesEvenement,
        nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
        erreurVisee: 'evenement_confondu_avec_issue',
    });
    return payload;
};

const genPhrasesQcm = () => {
    const p = tirerPhrases();
    const cible = randomChoice(['issue', 'evenement', 'ni']);
    const bonne = { issue: p.issue, evenement: p.evenement, ni: p.niNi }[cible];
    const question = {
        issue: 'Laquelle de ces phrases désigne une ISSUE de l\'expérience ?',
        evenement: 'Laquelle de ces phrases désigne un ÉVÈNEMENT réalisé par plusieurs issues ?',
        ni: 'Laquelle de ces phrases ne décrit pas un résultat du tirage ?',
    }[cible];
    const codes = { issue: 'evenement_confondu_avec_issue', evenement: 'evenement_confondu_avec_issue', ni: 'experience_aleatoire_mal_reconnue' };
    const dist = [p.issue, p.evenement, p.niNi].filter((x) => x !== bonne)
        .map((x) => ({ code: codes[cible], valeur: x }));
    const payload = makeQcm([bonne, ...dist.map((x) => x.valeur)], bonne,
        `${p.d.intro}<p>${question}</p>`, opts(L_PHRASES));
    if (p.d.el) payload.elements_additionnels = p.d.el;
    payload._meta.params = paramsDispo(p.d, 3, 'évènement', {
        nombre_d_issues_realisant_l_evenement: p.nbIssuesEvenement,
        nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
        distracteurs: dist,
    });
    return payload;
};

const genPhrasesVF = () => {
    const p = tirerPhrases();
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('evenement_confondu_avec_issue');
    if (!vs[1]) codes.push('evenement_elementaire_refuse');
    if (!vs[2]) codes.push('experience_aleatoire_mal_reconnue');
    if (!vs[3]) codes.push('nombre_au_lieu_de_la_liste');
    const lignes = [
        [`« ${p.evenement} » désigne un évènement réalisé par ${vs[0] ? 'deux issues' : 'une seule issue'}.`, vs[0] ? 0 : 1],
        [`« ${p.issue} » ${vs[1] ? 'désigne une issue, et c\'est aussi un évènement' : 'ne désigne ni une issue ni un évènement'}.`, vs[1] ? 0 : 1],
        [`« ${p.niNi} » ${vs[2] ? 'ne décrit pas un résultat du tirage' : 'décrit un résultat possible du tirage'}.`, vs[2] ? 0 : 1],
        [vs[3]
            ? 'Énumérer les issues d\'une expérience, c\'est les citer, et non donner seulement leur nombre.'
            : 'Énumérer les issues d\'une expérience, c\'est donner leur nombre.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${p.d.intro}<p>Vrai ou faux ?</p>`, opts(L_PHRASES));
    payload._meta.params = paramsDispo(p.d, 3, 'évènement', {
        nombre_d_issues_realisant_l_evenement: p.nbIssuesEvenement,
        nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ═════════════════════════════════════════════════════════════
// CARTE « L'évènement sur mesure » (niveau 4)
// ═════════════════════════════════════════════════════════════

const tirerMesure = () => randomChoice([tirerJetons, tirerUrne, tirerRoue])();

const genMesureQcm = () => {
    const d = tirerMesure();
    const ordre = shuffle(d.issues.map((_, x) => x));
    const deux = ordre.slice(0, 2).sort((a, b) => a - b);
    const une = ordre[2];
    const phraseDeux = `${cap(d.nomIssue(d.issues[deux[0]]))} ou ${d.issues[deux[1]]}`;
    const phraseUne = cap(d.nomIssue(d.issues[une]));
    const phraseToutes = `${cap(d.nomIssue(d.issues[0]))}, `
        + `${d.issues.slice(1, -1).join(', ')}${d.issues.length > 2 ? ' ou ' : ''}${d.issues[d.issues.length - 1]}`;
    const phraseAbsente = cap(d.nomIssue(d.absentes[0]));
    if (randInt(0, 1) === 0) {
        const dist = [
            { code: 'evenement_confondu_avec_issue', valeur: phraseUne },
            { code: 'evenement_confondu_avec_issue', valeur: phraseToutes },
            { code: 'evenement_confondu_avec_issue', valeur: phraseAbsente },
        ];
        const payload = makeQcm([phraseDeux, ...dist.map((x) => x.valeur)], phraseDeux,
            `${d.intro}<p>Quelle phrase décrit un évènement réalisé par EXACTEMENT deux issues ?</p>`,
            opts(L_MESURE));
        if (d.el) payload.elements_additionnels = d.el;
        payload._meta.params = paramsDispo(d, 4, 'évènement', {
            nombre_d_issues_realisant_l_evenement: 2,
            nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
            distracteurs: dist,
        });
        return payload;
    }
    const dist = [
        { code: 'evenement_elementaire_refuse', valeur: phraseDeux },
        { code: 'evenement_elementaire_refuse', valeur: phraseToutes },
        { code: 'evenement_confondu_avec_issue', valeur: phraseAbsente },
    ];
    const payload = makeQcm([phraseUne, ...dist.map((x) => x.valeur)], phraseUne,
        `${d.intro}<p>Quelle phrase décrit un évènement réalisé par UNE SEULE issue ?</p>`,
        opts(L_MESURE));
    if (d.el) payload.elements_additionnels = d.el;
    payload._meta.params = paramsDispo(d, 4, 'évènement', {
        nombre_d_issues_realisant_l_evenement: 1,
        nature_de_l_evenement: 'évènement réalisé par une seule issue',
        distracteurs: dist,
    });
    return payload;
};

const genMesureQcmM = () => {
    const d = tirerMesure();
    const ev = evenementCompose(d);
    const corrects = ev.indices.map((x) => cap(d.issues[x]));
    const choix = shuffle([...d.issues.map(cap), cap(d.absentes[0])]);
    const dist = [
        ...d.issues.filter((_, x) => !ev.indices.includes(x)).map((x) => ({ code: 'evenement_confondu_avec_issue', valeur: cap(x) })),
        { code: 'issue_confondue_avec_objet', valeur: cap(d.absentes[0]) },
    ];
    const payload = makeQcm(choix, corrects,
        `${d.intro}<p>On veut décrire l'évènement « ${ev.phrase} » par les issues qui le réalisent. `
        + 'Coche exactement ces issues. Attention : une proposition n\'est pas une issue de cette expérience.</p>',
        opts(L_MESURE), true);
    if (d.el) payload.elements_additionnels = d.el;
    payload._meta.params = paramsDispo(d, 4, 'issue', {
        nombre_d_issues_realisant_l_evenement: 2,
        nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
        distracteurs: dist,
    });
    return payload;
};

const genMesureVF = () => {
    const d = tirerMesure();
    const ev = evenementCompose(d);
    const i = randInt(0, d.issues.length - 1);
    const vs = equilibrer([0, 1, 2, 3].map(() => randInt(0, 1) === 0));
    const codes = [];
    if (!vs[0]) codes.push('evenement_elementaire_refuse');
    if (!vs[1]) codes.push('evenement_confondu_avec_issue');
    if (!vs[2]) codes.push('issue_confondue_avec_objet');
    if (!vs[3]) codes.push('nombre_au_lieu_de_la_liste');
    const lignes = [
        [vs[0]
            ? `Un évènement peut n'être réalisé que par une seule issue, comme « ${d.issueSeule(d.issues[i])} ».`
            : 'Un évènement doit toujours être réalisé par au moins deux issues.', vs[0] ? 0 : 1],
        [`L'évènement « ${ev.phrase} » est réalisé par ${vs[1] ? 'deux' : d.issues.length} issues.`, vs[1] ? 0 : 1],
        [`Cette expérience compte ${vs[2] ? d.issues.length : d.objets} issues, pour ${d.objets} ${d.nomObjet}s.`, vs[2] ? 0 : 1],
        [vs[3]
            ? 'Donner la liste des issues et donner leur nombre sont deux réponses différentes.'
            : 'Donner la liste des issues revient exactement à donner leur nombre.', vs[3] ? 0 : 1],
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((x) => x[0]), melange.map((x) => x[1]),
        `${d.intro}<p>Vrai ou faux ?</p>`, opts(L_MESURE));
    payload._meta.params = paramsDispo(d, 4, 'évènement', {
        nombre_d_issues_realisant_l_evenement: 2,
        nature_de_l_evenement: 'évènement réalisé par plusieurs issues',
        distracteurs: codes.map((code) => ({ code })),
    });
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    urne_qcm_unique: () => qcmVocabulaire(tirerUrne()),
    urne_qcm_multiple: () => qcmMultipleVocabulaire(tirerUrne()),
    urne_tableau_vf: () => vfVocabulaire(tirerUrne()),
    roue_qcm_unique: () => qcmVocabulaire(tirerRoue()),
    roue_qcm_multiple: () => qcmMultipleVocabulaire(tirerRoue()),
    roue_tableau_vf: () => vfVocabulaire(tirerRoue()),
    jetons_qcm_unique: () => qcmVocabulaire(tirerJetons()),
    jetons_qcm_multiple: () => qcmMultipleVocabulaire(tirerJetons()),
    jetons_tableau_vf: () => vfVocabulaire(tirerJetons()),
    phrases_classer: genPhrasesClasser,
    phrases_qcm_unique: genPhrasesQcm,
    phrases_tableau_vf: genPhrasesVF,
    mesure_qcm_unique: genMesureQcm,
    mesure_qcm_multiple: genMesureQcmM,
    mesure_tableau_vf: genMesureVF,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', ordonner: 'Ordonner', classer: 'Classer', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'urne', label: L_URNE, formatsList: buildFormats('urne') },
    { id: 'roue', label: L_ROUE, formatsList: buildFormats('roue') },
    { id: 'jetons', label: L_JETONS, formatsList: buildFormats('jetons') },
    { id: 'phrases', label: L_PHRASES, formatsList: buildFormats('phrases') },
    { id: 'mesure', label: L_MESURE, formatsList: buildFormats('mesure') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
